import { StyledSectionHeader } from "../../components/StyledSectionHeader";
import { useDispatch, useSelector } from "react-redux";

import {
    fetchComplianceIssues,
    IComplianceFilter,
    IFilter,
    ISorting,
    selectIssues,
} from "../../dashboard/DashboardSlice";
import { useCallback, useEffect, useMemo, useState } from "react";
import TablePageFooter, { DefaultPagination } from "../../components/TablePageFooter";
import HistoricInfractionsList from "./HistoricInfractionsList";
import UserAutocomplete, { UserSuggestion } from "../../components/UserAutoComplete";
import useInterval from "../../util/useInterval";
import { StyledModalItem } from "../../components/StyledModalItem";
import { StyledSelect } from "../../components/StyledSelect";
import { StyledSearchInput } from "../../components/StyledFilterInput";
import { StyledActionButton } from "../../components/StyledActionButton";
import { StyledFilterValue } from "../../components/StyledFilterValue";
import redcross from "../../assets/redcross.svg";
import handleDownload from "../../util/downloadCsv";
import { dashboardService } from "../../services/dashboardService";
import { IPagination } from "../../util/pagination";
import { downloadFromUrl } from "../../util/downloadFromUrl";

function HistoricInfractions(): JSX.Element {
    const dispatch = useDispatch();
    const infractions = useSelector(selectIssues);
    const company = useSelector((state: any) => state.auth.companyName);
    const version = useSelector((state: any) => state.auth.version);
    const cryptoVersion =
        version === "crypto_integration" ||
        version === "crypto_monitoring" ||
        version === "crossover_base" ||
        version === "crypto_base";

    const nextPageIssues = useSelector((state: any) => state.dashboard.nextPageIssues);
    const [pagination, setPagination] = useState<IPagination>(DefaultPagination());

    const defaultComplianceFilter: IComplianceFilter = {
        user: "",
        dateCreated: "",
        dateCreatedMin: "",
        dateCreatedMax: "",
        status: ["IN_REVIEW", "RESOLVED", "PENDING", "MARKED"],
    };

    const [filterMultiple, setFilterMultiple] = useState(
        defaultComplianceFilter
    ) as any;
    const [filter, setFilter] = useState("employee");
    const [search, setSearch] = useState("");
    const [startSearch, setStartSearch] = useState("");
    const [endSearch, setEndSearch] = useState("");
    const [rangeBasedFiltering, setRangeBasedFiltering] = useState(false);

    const selectUser = (user: UserSuggestion | undefined) => {
        if (user !== undefined) {
            filterMultiple["user"] = user.name;
            setFilterMultiple({ ...filterMultiple });
        } else {
            filterMultiple["user"] = defaultComplianceFilter.user;
            setFilterMultiple({ ...filterMultiple });
        }
        setSearch("");
    };

    const [issuesSorting, setIssuesSorting] = useState<ISorting>({
        field: "dateCreated",
        direction: "desc",
    });

    const issuesSortingByDate: ISorting[] = useMemo(() => {
        if (issuesSorting.field === "dateCreated") return [issuesSorting];
        return [issuesSorting, { field: "dateCreated", direction: "desc" }];
    }, [issuesSorting]);

    const fetch = useCallback(() => {
        dispatch(
            fetchComplianceIssues({
                ...pagination,
                filter: filterMultiple,
                sorting: issuesSortingByDate,
            })
        );
    }, [dispatch, pagination, issuesSortingByDate, filterMultiple]);

    useEffect(fetch, [fetch]);

    useInterval(fetch, 25000);

    const handleSubmitNote = (note: string, issueId: string) => {
        if (note !== "") {
            dashboardService.createComplianceIssueNote(note, issueId).then((r) => {
                if (r.status === 200) {
                    dispatch(
                        fetchComplianceIssues({
                            ...pagination,
                            filter: {
                                ...defaultComplianceFilter,
                                status: ["PENDING", "IN_REVIEW"],
                            },
                            sorting: issuesSortingByDate,
                        })
                    );
                }
            });
        }
    };

    /* Filtering */
    const filterOptions = useMemo(() => {
        return (
            <>
                <option value="employee">Employee</option>
                <option value="ticker">Ticker</option>
                <option value="asset">Asset</option>
                <option value="volume">Volume</option>
                <option value="status">Status</option>
                {company === "Public" && (
                    <option value="dateCreated">Date Created</option>
                )}
            </>
        );
    }, [company]);

    const dateTypes = ["dateCreated"];

    function handleKeyDown(event: any) {
        if (event.key === "Enter") {
            filterMultiple[filter] = search;
            setFilterMultiple({ ...filterMultiple });
            setSearch("");
        }
    }

    function handleOnClick() {
        setFilterMultiple(defaultComplianceFilter);
    }

    function removeFilter(txt: any) {
        filterMultiple[txt] = "";
        setFilterMultiple({ ...filterMultiple });
    }

    function handleRangeSearch() {
        if (startSearch !== "" && endSearch !== "") {
            filterMultiple[filter + "Min"] = startSearch;
            filterMultiple[filter + "Max"] = endSearch;
            setFilterMultiple({ ...filterMultiple });
            setStartSearch("");
            setEndSearch("");
        }
    }

    return (
        <div>
            <div
                style={{
                    display: "flex",
                    flexDirection: "row",
                    marginTop: "3%",
                    marginBottom: "3%",
                }}
            >
                <StyledModalItem style={{ display: "flex", width: "50%" }}>
                    <StyledSelect
                        id="filterValue"
                        name="filterValue"
                        onChange={(event) => {
                            setFilter(event.target.value);
                        }}
                    >
                        {filterOptions}
                    </StyledSelect>
                </StyledModalItem>

                <div style={{ width: "50%" }}>
                    {!rangeBasedFiltering ? (
                        <>
                            {filter === "user" || filter === "employee" ? (
                                <div style={{ position: "relative" }}>
                                    <UserAutocomplete
                                        value={search}
                                        onSelect={selectUser}
                                        onEnter={selectUser}
                                        style={{ width: "100%" }}
                                        placeholder={"Filter by name or email"}
                                    />
                                </div>
                            ) : (
                                <StyledSearchInput
                                    placeholder="Search Historic Infractions"
                                    type={dateTypes.includes(filter) ? "date" : "text"}
                                    value={search}
                                    onKeyDown={handleKeyDown}
                                    onChange={(event) => setSearch(event.target.value)}
                                    style={{ display: "flex" }}
                                />
                            )}
                            {dateTypes.includes(filter) || filter === "volume" ? (
                                <div
                                    onClick={() => setRangeBasedFiltering(true)}
                                    style={{
                                        textAlign: "right",
                                        fontSize: "12px",
                                        textDecoration: "underline",
                                        color: "#2c71f0",
                                        cursor: "pointer",
                                    }}
                                >
                                    Add range based filter
                                </div>
                            ) : null}
                        </>
                    ) : (
                        <>
                            <div style={{ display: "flex", flexDirection: "row" }}>
                                <StyledSearchInput
                                    placeholder="Start Filter"
                                    type={dateTypes.includes(filter) ? "date" : "text"}
                                    value={startSearch}
                                    onChange={(event) =>
                                        setStartSearch(event.target.value)
                                    }
                                    style={{ display: "flex", width: "35%" }}
                                />
                                &nbsp;_&nbsp;
                                <StyledSearchInput
                                    placeholder="End Filter"
                                    type={dateTypes.includes(filter) ? "date" : "text"}
                                    value={endSearch}
                                    onChange={(event) =>
                                        setEndSearch(event.target.value)
                                    }
                                    style={{ display: "flex", width: "35%" }}
                                />
                                &nbsp;&nbsp;
                                <StyledActionButton
                                    onClick={handleRangeSearch}
                                    style={{ width: "20%" }}
                                >
                                    Enter
                                </StyledActionButton>
                            </div>
                            {dateTypes.includes(filter) || filter === "volume_max" ? (
                                <div
                                    onClick={() => setRangeBasedFiltering(false)}
                                    style={{
                                        textAlign: "right",
                                        fontSize: "12px",
                                        textDecoration: "underline",
                                        color: "#2c71f0",
                                        cursor: "pointer",
                                    }}
                                >
                                    Cancel range based filtering
                                </div>
                            ) : null}
                        </>
                    )}
                </div>
                <StyledActionButton
                    style={{ width: "20%", marginLeft: "6%" }}
                    onClick={handleOnClick}
                >
                    Clear Filters
                </StyledActionButton>
            </div>
            <br />
            <div style={{ display: "flex", flexDirection: "row", marginBottom: "3%" }}>
                {Object.keys(filterMultiple) &&
                    Object.keys(filterMultiple).map((txt: any, index: any) => (
                        <>
                            {filterMultiple[txt] !== "" && txt !== "status" ? (
                                <StyledFilterValue key={index}>
                                    &nbsp;&nbsp;&nbsp;{txt}: {filterMultiple[txt]}
                                    <img
                                        style={{
                                            marginRight: "4px",
                                            cursor: "pointer",
                                        }}
                                        src={redcross}
                                        alt=""
                                        onClick={() => removeFilter(txt)}
                                    />
                                </StyledFilterValue>
                            ) : null}
                        </>
                    ))}
            </div>
            <StyledSectionHeader>Historic Infractions</StyledSectionHeader>
            <div
                onClick={() =>
                    handleDownload(
                        dashboardService.getComplianceIssuesDownload(
                            {
                                ...filterMultiple,
                            },
                            issuesSortingByDate,
                            cryptoVersion
                                ? ["walletAddress", "counterPartyAddress", "from", "to"]
                                : []
                        ),
                        "historic_infractions"
                    )
                }
                style={{
                    textAlign: "right",
                    fontSize: "15px",
                    textDecoration: "underline",
                    color: "#2c71f0",
                    cursor: "pointer",
                }}
            >
                Download historic infractions
            </div>

            <HistoricInfractionsList
                issues={infractions}
                setSorting={setIssuesSorting}
                sorting={issuesSorting}
                handleSubmitNote={handleSubmitNote}
            />
            <TablePageFooter
                nextPage={nextPageIssues}
                pagination={pagination}
                setPageNumber={(n) => setPagination({ ...pagination, pageNumber: n })}
                setPageSize={(s) => setPagination({ ...pagination, pageSize: s })}
            />
        </div>
    );
}

export default HistoricInfractions;
